import { Alert } from "@/components/Alert"
import { FormCheckbox } from "@/components/Form/Checkbox"
import { FormCountrySelect } from "@/components/Form/CoutrySelect"
import { Input } from "@/components/Form/Input"
import { Title } from "@/components/Title"
import { cn, getFormattedPhone } from "@/utils"
import { selectUser, useStoreClient } from "@usher/pe-client-front-end-lib"
import { CorporateUser } from "@usher/pe-client-front-end-lib/dist/ExClient"
import { PropsWithChildren } from "react"
import { Control, FieldValues, Path, useFormContext } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"

const Section = ({ className, title, children }: { title: string; className?: string } & PropsWithChildren) => {
  return (
    <div className={className}>
      <div className="mb-2.5">{title}</div>
      <div className="font-bold text-black">{children}</div>
    </div>
  )
}
export const ExpenseDeliveryAddress = <T extends FieldValues>({ control, isRain }: { control: Control<T>; isRain: boolean }) => {
  const { $t: t } = useIntl()
  const account = useStoreClient(selectUser)
  const { watch } = useFormContext()
  const useCompany = watch("use_company")
  const selectedUser = watch("user") as { meta?: CorporateUser }
  const user = isRain ? selectedUser?.meta : account
  return (
    <div className={cn("bg-[#FCFCFC mt-4")}>
      <Title variant="body1">
        <FormattedMessage id="delivery" />
      </Title>
      <Alert className="my-2">
        <strong>Important:</strong> please ensure you provide the correct address when ordering your card, as it cannot be changed once the card is
        ordered.
      </Alert>
      <div className="border-b border-gray-200 my-2.5" />
      <FormCheckbox control={control} name={"use_company" as Path<T>} label={t({ id: "data-from-profile" })} className="mb-4" />
      {useCompany && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-[20px] text-sm">
          <Section title={t({ id: "pa_187" })}>{user?.first_name ?? "-"}</Section>
          <Section title={t({ id: "pa_188" })}>{user?.last_name ?? "-"}</Section>
          <Section title={t({ id: "pa_192" })}>{user?.address ?? "-"}</Section>
          {/*@ts-ignore*/}
          <Section title={t({ id: "pa_190" })}>{isRain ? (user?.country ?? "-") : (user?.country_name ?? "-")}</Section>
          <Section title={t({ id: "pa_193" })}>{user?.state ?? "-"}</Section>
          <Section title={t({ id: "pa_194" })}>{user?.city ?? "-"}</Section>
          <Section title={t({ id: "apt/suit" })}>{user?.app ?? "-"}</Section>
          <Section title={t({ id: "pa_186" })}>{getFormattedPhone(user?.phone ?? "").formatted ?? "-"}</Section>
          <Section title={`ZIP / ${t({ id: "pa_191" })}`}>{user?.zip ?? "-"}</Section>
        </div>
      )}

      {!useCompany && (
        <div className="grid gap-2.5">
          <Input.Form
            control={control}
            rules={{
              maxLength: { value: 20, message: "Maximum 20" },
              validate: (v) => (/^[a-zA-Z]+$/.test(v) ? undefined : "Only latin characters"),
            }}
            name={"firstName" as Path<T>}
            placeholder={t({ id: "pa_187" })}
            required
          />
          <Input.Form
            control={control}
            rules={{
              maxLength: { value: 20, message: "Maximum 20" },
              validate: (v) => (/^[a-zA-Z]+$/.test(v) ? undefined : "Only latin characters"),
            }}
            name={"lastName" as Path<T>}
            placeholder={t({ id: "pa_188" })}
            required
          />
          <Input.Phone withFlag control={control} name={"mobile" as Path<T>} placeholder={t({ id: "pa_186" })} />
          <FormCountrySelect searchable onlyValue control={control} name={"countryCode" as Path<T>} label="Country" required />
          <Input.Form
            rules={{ maxLength: { value: 27, message: "Maximum 27" } }}
            control={control}
            name={"state" as Path<T>}
            placeholder={t({ id: "pa_193" })}
          />
          <Input.Form
            rules={{ maxLength: { value: 100, message: "Maximum 100" } }}
            control={control}
            name={"address1" as Path<T>}
            placeholder={t({ id: "pa_192" })}
            required
          />
          <Input.Form
            rules={{ maxLength: { value: 27, message: "Maximum 27" } }}
            control={control}
            name={"address2" as Path<T>}
            placeholder={t({ id: "apt/suit" })}
            required
          />
          <Input.Form
            rules={{ maxLength: { value: 27, message: "Maximum 27" } }}
            control={control}
            name={"city" as Path<T>}
            placeholder={t({ id: "pa_194" })}
            required
          />
          <Input.Form
            rules={{ maxLength: { value: 27, message: "Maximum 27" } }}
            control={control}
            name={"PostalCode" as Path<T>}
            placeholder={`ZIP / ${t({ id: "pa_191" })}`}
            required
          />
        </div>
      )}
    </div>
  )
}
